
import React, { useRef, useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { SearchIcon, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onClear?: () => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}

const SearchInput: React.FC<SearchInputProps> = ({
  value,
  onChange,
  onFocus,
  onBlur,
  onClear,
  placeholder = "Tìm kiếm người tham dự...",
  className,
  autoFocus = false
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();
  const [isFocused, setIsFocused] = useState(autoFocus);
  
  // Initial focus if autoFocus is true
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
        
        // On mobile, also trigger click to show virtual keyboard
        if (isMobile) {
          inputRef.current?.click();
        }
      }, 100);
    }
  }, [autoFocus, isMobile]);
  
  const handleFocus = () => {
    setIsFocused(true);
    if (onFocus) onFocus();
  };
  
  const handleBlur = () => {
    setIsFocused(false);
    if (onBlur) onBlur();
  };
  
  const handleClear = () => {
    onChange('');
    if (onClear) onClear();
    
    // Refocus the input after clearing
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };
  
  return (
    <div className="relative flex-1">
      <SearchIcon 
        className={cn(
          "absolute text-amber-500 dark:text-amber-400 z-10",
          isMobile ? "left-2.5 top-2.5 h-4 w-4" : "left-3 top-3 h-4 w-4"
        )} 
      />
      <Input
        ref={inputRef}
        type="search"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={cn(
          isMobile ? "h-9 text-sm pl-9 pr-8 rounded-xl" : "pl-10 pr-9 rounded-xl", 
          "bg-white dark:bg-gray-800", // Changed from transparent to white background
          "border-amber-200/70 dark:border-amber-700/30", 
          "focus:border-amber-400 focus:ring-2 focus:ring-amber-400/30 dark:focus:border-amber-500 dark:focus:ring-amber-500/30",
          "transition-all duration-300",
          "placeholder:text-slate-400/90 dark:placeholder:text-slate-500/80",
          "shadow-sm hover:shadow-md",
          isFocused && "shadow-md",
          className
        )}
        lang="vi"
        autoCapitalize="off"
        autoComplete="off"
        autoCorrect="off"
        spellCheck="false"
        inputMode="search"
        aria-label="Tìm kiếm"
      />
      {value && (
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={handleClear}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full p-0 hover:bg-amber-100/60 dark:hover:bg-amber-800/30"
          aria-label="Xóa từ khóa tìm kiếm"
          type="button"
        >
          <X className={cn(
            "text-amber-500 dark:text-amber-400",
            isMobile ? "h-3.5 w-3.5" : "h-4 w-4"
          )} />
        </Button>
      )}
    </div>
  );
};

export default SearchInput;
